import {
  customFetch,
  fetchBlob,
  getImportsControllerCreateImportUrl,
  getImportsControllerDownloadFileUrl,
  getImportsControllerGetImportQueryKey,
  getImportsControllerListImportsQueryKey,
  importsControllerGetImport,
  importsControllerLatestMapping,
  importsControllerListImports,
  importsControllerRetry,
  importsControllerUpdateMapping,
  type ImportListItemDto,
  type ImportResponseDto,
  type LatestMappingResponseDto,
  type UpdateImportMappingDto,
} from '@artic/api-client';
import { keepPreviousData, queryOptions, useMutation, useQueryClient } from '@tanstack/react-query';

import { expectData } from '../_shared/api';
import { downloadBlob } from '../_shared/download';
import type { ImportStatus } from './search';
import type { ImportMapping } from './transform';

const LIST_POLL_INTERVAL_MS = 2_000;
const DETAIL_POLL_INTERVAL_MS = 2_000;

/** Statuses that will not change without user action — polling can stop. */
const TERMINAL_STATUSES: ReadonlySet<ImportResponseDto['status']> = new Set(['error', 'success']);

function isTerminal(status: ImportResponseDto['status']): boolean {
  return TERMINAL_STATUSES.has(status);
}

// ── List (with polling while any import is in flight) ─────────────

export function importsListQueryOptions(
  partnerId: string | undefined,
  filters: { status?: ImportStatus } = {},
) {
  const params = {
    partner_id: partnerId ?? '',
    ...(filters.status ? { status: filters.status } : {}),
  };
  return queryOptions({
    queryKey: getImportsControllerListImportsQueryKey(params),
    queryFn: async ({ signal }): Promise<ImportListItemDto[]> => {
      const res = await importsControllerListImports(params, { signal });
      return expectData<ImportListItemDto[]>(res);
    },
    enabled: !!partnerId,
    staleTime: 5_000,
    placeholderData: keepPreviousData,
    // Poll while any row is still pending / in-progress; stop once every row
    // has reached a terminal status.
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return false;
      return data.some((row) => !isTerminal(row.status)) ? LIST_POLL_INTERVAL_MS : false;
    },
  });
}

// ── Detail (polling while non-terminal) ───────────────────────────

export function importDetailQueryOptions(importId: string | undefined) {
  return queryOptions({
    queryKey: getImportsControllerGetImportQueryKey(importId ?? ''),
    queryFn: async ({ signal }): Promise<ImportResponseDto> => {
      const res = await importsControllerGetImport(importId ?? '', { signal });
      return expectData<ImportResponseDto>(res);
    },
    enabled: !!importId,
    staleTime: 1_000,
    placeholderData: keepPreviousData,
    refetchInterval: (query) => {
      const data = query.state.data;
      if (!data) return DETAIL_POLL_INTERVAL_MS;
      return isTerminal(data.status) ? false : DETAIL_POLL_INTERVAL_MS;
    },
  });
}

// ── Partner template mapping (previous-upload seed) ───────────────

export function latestMappingQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: ['imports-latest-mapping', partnerId] as const,
    queryFn: async ({ signal }): Promise<ImportMapping> => {
      const res = await importsControllerLatestMapping({ partner_id: partnerId ?? '' }, { signal });
      return expectData<LatestMappingResponseDto>(res).mapping as ImportMapping;
    },
    enabled: !!partnerId,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

/**
 * The partner's most-recent SUCCESSFUL import mapping — the source for the
 * "copy mapping from most recent success" button, so a newly-arrived file can
 * reuse a known-good mapping instead of being mapped from scratch. Empty when
 * the partner has no successful import yet.
 */
export function latestSuccessfulMappingQueryOptions(partnerId: string | undefined) {
  return queryOptions({
    queryKey: ['imports-latest-successful-mapping', partnerId] as const,
    queryFn: async ({ signal }): Promise<ImportMapping> => {
      const res = await importsControllerLatestMapping(
        { partner_id: partnerId ?? '', status: 'success' },
        { signal },
      );
      return expectData<LatestMappingResponseDto>(res).mapping as ImportMapping;
    },
    enabled: !!partnerId,
    staleTime: 5 * 60_000,
    placeholderData: keepPreviousData,
  });
}

// ── Mutations ─────────────────────────────────────────────────────

export interface CreateImportInput {
  partnerId: string;
  file: File;
  mapping: ImportMapping;
  /** Fallback project for rows without a `project.code`, and default target. */
  projectId?: string | null;
}

/**
 * Upload an import. The generated `createImport` hook JSON-serialises the body
 * (wrong for multipart) and per-element-appends the mapping, so we build the
 * `FormData` by hand and POST through `customFetch` — the same network layer
 * (auth token, partner header, 401-refresh-retry) the generated hooks use. We
 * deliberately do NOT set `Content-Type` so the browser sets the multipart
 * boundary; the backend's `FastifyFilesInterceptor` JSON-parses the single
 * `mapping` field back into the array.
 */
export function useCreateImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (input: CreateImportInput): Promise<ImportResponseDto> => {
      const formData = new FormData();
      formData.append('file', input.file);
      formData.append('mapping', JSON.stringify(input.mapping));
      if (input.projectId) formData.append('project_id', input.projectId);

      const res = await customFetch<{ data: ImportResponseDto }>(
        getImportsControllerCreateImportUrl({ partner_id: input.partnerId }),
        { method: 'POST', body: formData },
      );
      return expectData<ImportResponseDto>(res);
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: getImportsControllerListImportsQueryKey() });
    },
  });
}

export function useUpdateImportMapping() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (vars: {
      importId: string;
      mapping: ImportMapping;
    }): Promise<ImportResponseDto> => {
      // The generated DTO types `unit` as an exact-optional `string`; the client
      // zod model widens it to `string | undefined`. The shapes are structurally
      // identical on the wire (JSON), so assert across the boundary.
      const body = { mapping: vars.mapping } as unknown as UpdateImportMappingDto;
      const res = await importsControllerUpdateMapping(vars.importId, body);
      return expectData<ImportResponseDto>(res);
    },
    onSuccess: (_data, vars) => {
      void queryClient.invalidateQueries({
        queryKey: getImportsControllerGetImportQueryKey(vars.importId),
      });
      void queryClient.invalidateQueries({ queryKey: getImportsControllerListImportsQueryKey() });
    },
  });
}

export function useRetryImport() {
  const queryClient = useQueryClient();
  return useMutation({
    mutationFn: async (importId: string): Promise<ImportResponseDto> => {
      const res = await importsControllerRetry(importId);
      return expectData<ImportResponseDto>(res);
    },
    onSuccess: (_data, importId) => {
      void queryClient.invalidateQueries({
        queryKey: getImportsControllerGetImportQueryKey(importId),
      });
      void queryClient.invalidateQueries({ queryKey: getImportsControllerListImportsQueryKey() });
    },
  });
}

// ── Binary download (stored original file) ────────────────────────

export async function downloadImportFile(importId: string, filename: string): Promise<void> {
  const blob = await fetchBlob(getImportsControllerDownloadFileUrl(importId));
  downloadBlob(blob, filename);
}
